<script setup>
/**
 * Batch Add/Update Quiz — parses a CSV and upserts each row via /api/quiz/batchadd.
 * Uses native CSV parsing (no papaparse dependency).
 */
import { ref } from "vue";
import { useRouter } from "vue-router";
import { saveQuiz } from "./api";

const router = useRouter();
const csvFileName = ref(""), rows = ref([]), loading = ref(false), success = ref(false), error = ref("");

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]/g, ""));
  return lines.slice(1).map(line => {
    const vals = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (vals[i] ?? "").trim()]));
  }).filter(r => r.code);
}

function toQuiz(info) {
  const get = (keys) => { for (const k of keys) { const nk = k.toLowerCase().replace(/[^a-z0-9]/g, ""); if (info[nk] !== undefined) return String(info[nk] || "").trim(); } return ""; };
  const lang = get(["language","lang"]).toLowerCase() === "english" ? "en" : "zh";
  const level = get(["level","levels"]).split(",").map(s => s.trim()).filter(Boolean);
  const rawType = get(["type","quiztype"]);
  const isMc = rawType.toLowerCase() === "mcq";
  const type = isMc ? "mc" : rawType;
  const title = isMc ? (lang === "en" ? "Multiple Choice" : "综合填空") : (lang === "en" ? "Fill in the Blanks" : "填空题");
  const responsesRaw = get(["responses","response","options"]);
  const answerRaw = get(["correctanswer","correctans","answer"]);
  const responses = isMc ? responsesRaw.split(";").map(s => s.trim()).filter(Boolean) : responsesRaw;
  const answer = isMc ? answerRaw.trim() : answerRaw.split(";").map(s => s.trim()).filter(Boolean);
  return { code: info.code, level, lang, title, type, questions: [{ competency: get(["competency","competencies"]), text: get(["questiontext","question"]), responses, answer, type }], enable: get(["enable","enabled"]).toLowerCase() === "true" };
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => { rows.value = parseCsv(ev.target.result); csvFileName.value = file.name; };
  reader.readAsText(file);
}

async function submit() {
  error.value = ""; success.value = "";
  if (!csvFileName.value) { error.value = "Please include a CSV file."; return; }
  loading.value = true;
  try {
    const valid = rows.value.filter(r => r.code && r.level);
    await Promise.all(valid.map(r => saveQuiz(toQuiz(r))));
    success.value = true;
  } catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'quizzes' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Batch Add/Update Quiz</h1>
    <VCard class="pa-4" max-width="560">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Batch add/update successful.</VAlert>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">CSV File (List of Quiz)</div>
        <input type="file" accept=".csv" @change="onFile" />
        <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ rows.length }} row(s)</p>
      </div>
      <VBtn :loading="loading" :disabled="!csvFileName" @click="submit">Submit</VBtn>
    </VCard>
  </section>
</template>
